import React, { useState, useEffect, useRef } from 'react'
import type { Course } from '@/types/course'
import type { CreateCourseRequest, UpdateCourseRequest } from '@/types/api'
import { uploadService } from '@/services/uploadService'

interface CourseFormProps {
  onSubmit: (data: CreateCourseRequest | UpdateCourseRequest) => void
  onCancel?: () => void
  initialData?: Course
  isLoading?: boolean
}

interface FormData {
  title: string
  slug: string
  description: string
  courseType: 'pure_online' | 'hybrid'
  price: string
  thumbnailUrl: string
}

interface FormErrors {
  title?: string
  slug?: string
  description?: string
  price?: string
}

// Format number to Indonesian Rupiah format (e.g., 1.500.000)
const formatRupiah = (value: string): string => {
  // Remove all non-digit characters
  const numericValue = value.replace(/\D/g, '')
  // Format with thousand separators
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

// Parse formatted Rupiah string back to number
const parseRupiah = (value: string): number => {
  return parseInt(value.replace(/\./g, ''), 10) || 0
}

// Generate slug from title
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove consecutive hyphens
    .trim()
}

export const CourseForm: React.FC<CourseFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    description: initialData?.description || '',
    courseType: initialData?.courseType || 'pure_online',
    price: initialData?.price ? formatRupiah(initialData.price.toString()) : '',
    thumbnailUrl: initialData?.thumbnailUrl || '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [autoSlug, setAutoSlug] = useState(!initialData?.slug)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-generate slug from title if autoSlug is enabled
  useEffect(() => {
    if (autoSlug && formData.title) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(formData.title),
      }))
    }
  }, [formData.title, autoSlug])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    const price = parseRupiah(formData.price)
    if (formData.price && price < 0) {
      newErrors.price = 'Price must be 0 or greater'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const res = await uploadService.uploadImage(file)
      if (!res.success) throw new Error(res.error?.message || 'Gagal upload gambar')
      
      setFormData(prev => ({
        ...prev,
        thumbnailUrl: res.data!.url
      }))
    } catch (err) {
      alert((err as Error).message)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const data: CreateCourseRequest | UpdateCourseRequest = {
      title: formData.title,
      slug: formData.slug,
      description: formData.description,
      courseType: formData.courseType,
      price: parseRupiah(formData.price),
      thumbnailUrl: formData.thumbnailUrl,
    }

    onSubmit(data)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatRupiah(e.target.value)
    setFormData((prev) => ({
      ...prev,
      price: formatted,
    }))

    if (errors.price) {
      setErrors((prev) => ({
        ...prev,
        price: undefined,
      }))
    }
  }

  const inputClasses = "w-full pl-11 pr-4 py-3.5 bg-white/50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 focus:border-primary/50 focus:ring-4 focus:ring-primary/5 rounded-xl text-sm font-bold text-slate-900 dark:text-white transition-all outline-none placeholder:text-slate-400 placeholder:font-medium shadow-sm"
  const labelClasses = "block text-xs font-black text-slate-700 dark:text-slate-300 tracking-wider mb-2 ml-1"

  return (
    <form onSubmit={handleSubmit} className="course-form space-y-6">
      {/* Thumbnail Upload */}
      <div className="space-y-1.5">
        <label className={labelClasses}>Sampul Kursus</label>
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleThumbnailChange} 
          accept="image/*" 
          className="hidden" 
        />
        <div 
          onClick={() => fileInputRef.current?.click()}
          className={`relative aspect-video rounded-2xl border-2 border-dashed border-slate-200 dark:border-white/10 overflow-hidden cursor-pointer group transition-all ${isUploading ? 'opacity-50' : 'hover:border-primary/50 bg-slate-50/50 dark:bg-white/[0.02]'}`}
        >
          {formData.thumbnailUrl ? (
            <>
              <img 
                src={formData.thumbnailUrl} 
                alt="Course Preview" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="size-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                  <span className="material-symbols-outlined">photo_camera</span>
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
              <span className="material-symbols-outlined text-[48px]">image</span>
              <span className="text-xs font-black tracking-widest">Klik untuk Upload Sampul</span>
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center">
              <div className="animate-spin size-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 font-bold ml-1 italic">* Rekomendasi ukuran 1280x720px (16:9)</p>
      </div>

      {/* Title Field */}
      <div className="space-y-1">
        <label htmlFor="title" className={labelClasses}>
          Judul Kursus
        </label>
        <div className="relative group">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">title</span>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={inputClasses}
            placeholder="Masukkan judul pelatihan..."
          />
        </div>
        {errors.title && <p className="mt-1 text-[11px] font-bold text-rose-500 ml-1">{errors.title}</p>}
      </div>

      {/* Slug Field */}
      <div className="space-y-1">
        <label htmlFor="slug" className={labelClasses}>
          URL Slug
        </label>
        <div className="flex gap-2">
          <div className="relative group flex-1">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">link</span>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={(e) => {
                setAutoSlug(false)
                handleChange(e)
              }}
              className={inputClasses}
              placeholder="url-pelatihan"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setAutoSlug(true)
              setFormData(prev => ({ ...prev, slug: generateSlug(prev.title) }))
            }}
            className="px-5 py-2.5 bg-slate-100 dark:bg-white/5 hover:bg-primary hover:text-white rounded-xl text-xs font-black tracking-widest transition-all border border-slate-200 dark:border-white/10"
          >
            Auto
          </button>
        </div>
      </div>

      {/* Description Field */}
      <div className="space-y-1">
        <label htmlFor="description" className={labelClasses}>
          Deskripsi
        </label>
        <div className="relative group">
          <span className="absolute left-3.5 top-4 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">description</span>
          <textarea
            id="description"
            name="description"
            rows={4}
            value={formData.description}
            onChange={handleChange}
            className={`${inputClasses} pl-11 resize-none`}
            placeholder="Tulis deskripsi kursus di sini..."
          />
        </div>
        {errors.description && <p className="mt-1 text-[11px] font-bold text-rose-500 ml-1">{errors.description}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Course Type */}
        <div className="space-y-1">
          <label htmlFor="courseType" className={labelClasses}>
            Tipe
          </label>
          <div className="relative group">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">category</span>
            <select
              id="courseType"
              name="courseType"
              value={formData.courseType}
              onChange={handleChange}
              className={`${inputClasses} appearance-none cursor-pointer`}
            >
              <option value="pure_online">Pure Online</option>
              <option value="hybrid">Hybrid (Kombinasi)</option>
            </select>
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">expand_more</span>
          </div>
        </div>

        {/* Price Field */}
        <div className="space-y-1">
          <label htmlFor="price" className={labelClasses}>
            Harga (IDR)
          </label>
          <div className="relative group">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-slate-400 text-xs transition-colors group-focus-within:text-primary">Rp</span>
            <input
              type="text"
              id="price"
              name="price"
              value={formData.price}
              onChange={handlePriceChange}
              placeholder="0"
              className={inputClasses}
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100 dark:border-white/5">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-slate-500 dark:text-slate-400 text-sm font-black tracking-widest hover:text-slate-900 dark:hover:text-white transition-all"
          >
            Batal
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="px-10 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-sm font-black tracking-[0.1em] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none disabled:opacity-50"
        >
          {isLoading ? 'Memproses...' : initialData ? 'Simpan Perubahan' : 'Buat Kursus'}
        </button>
      </div>
    </form>
  )
}
